import { useState, useEffect } from "react";
import { fetchTrendingBooks } from "../../api/trending-books";
import { TrendingBook } from "../../api/trending-books";
import BookCard from "./BookCard";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { TrendingUp, Clock, Calendar } from "lucide-react";
import { Skeleton } from "../ui/skeleton";

interface TrendingBooksProps {
  limit?: number;
  period?: "24h" | "7d" | "30d" | "90d";
  category?: number;
  showPeriodSelector?: boolean;
  className?: string;
}

export const TrendingBooks: React.FC<TrendingBooksProps> = ({
  limit = 10,
  period = "24h",
  category,
  showPeriodSelector = false,
  className = "",
}) => {
  const [trendingBooks, setTrendingBooks] = useState<TrendingBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<
    "24h" | "7d" | "30d" | "90d"
  >(period);

  useEffect(() => {
    const loadTrendingBooks = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchTrendingBooks({
          limit,
          period: selectedPeriod,
          category,
        });

        setTrendingBooks(response.trending_books);
      } catch (err) {
        console.error("Error loading trending books:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load trending books"
        );
      } finally {
        setLoading(false);
      }
    };

    loadTrendingBooks();
  }, [limit, selectedPeriod, category]);

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case "24h":
        return "24 Hours";
      case "7d":
        return "7 Days";
      case "30d":
        return "30 Days";
      case "90d":
        return "3 Months";
      default:
        return period;
    }
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-orange-500" />
          <h2 className="text-xl font-semibold">Trending Books</h2>
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: limit }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Unable to Load Trending Books
            </h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trendingBooks.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Trending Books
            </h3>
            <p className="text-gray-500">
              Check back later for trending books in the last{" "}
              {getPeriodLabel(selectedPeriod)}.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-orange-500" />
          <h2 className="text-xl font-semibold">Trending Books</h2>
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {getPeriodLabel(selectedPeriod)}
          </Badge>
        </div>

        {showPeriodSelector && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <div className="flex gap-1">
              <Button
                variant={selectedPeriod === "24h" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("24h")}
                className="text-xs"
              >
                24h
              </Button>
              <Button
                variant={selectedPeriod === "7d" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("7d")}
                className="text-xs"
              >
                7d
              </Button>
              <Button
                variant={selectedPeriod === "30d" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("30d")}
                className="text-xs"
              >
                30d
              </Button>
              <Button
                variant={selectedPeriod === "90d" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod("90d")}
                className="text-xs"
              >
                90d
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Trending Books List */}
      <div className="space-y-2">
        {trendingBooks.map((book, index) => (
          <div key={book.id} className="relative">
            {/* Rank Badge */}
            <div className="absolute top-3 right-3 z-10">
              <Badge variant="secondary" className="text-xs font-bold">
                #{index + 1}
              </Badge>
            </div>

            {/* Book Card */}
            <BookCard
              book={{
                id: book.id,
                title: book.title,
                authors:
                  book.authors?.map((author) => ({
                    id: author.id,
                    name: author.name,
                    roles: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  })) || [],
                coverImage: book.cover_image,
                coverBgColor: book.cover_bg_color,
                categories:
                  book.categories?.map((category) => ({
                    id: category.id,
                    name: category.name,
                    is_active: true,
                    isActive: true,
                  })) || [],
                readingTime: book.reading_time,
                rating: book.rating,
                viewCount: book.view_count || 0,
                read_count: book.read_count,
                audio_play_count: book.audio_play_count,
                summaryContents:
                  book.summary_contents?.map((content) => ({
                    id: content.id,
                    bookId: book.id,
                    language: "en",
                    chapters: [],
                    audioUrl: content.audio_url,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  })) || [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isActive: true,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
